/**
 * milestoneCelebration — the "full moment" trio (Feb 24 2026 Karine ask):
 *   1. subtle gold confetti burst from the pill's on-screen position
 *   2. luxe toast: "You just unlocked $50 credit!"
 *   3. (chord + pulse handled elsewhere — this module just pairs them)
 *
 * All three fire together the first time a signed-in client's balance
 * crosses a redemption threshold. Fails silent on unsupported browsers
 * (no confetti canvas, no toast). Uses `sonner`'s custom render so the
 * message reads as champagne-on-espresso instead of the default green
 * success toast — matches the site's Playfair + Crown Reserve palette.
 */
import confetti from "canvas-confetti";
import { toast } from "sonner";

// Reuse a single dedicated canvas so several rapid milestones don't
// stack full-screen canvases (would happen on double-crossing).
let _instance = null;
function getInstance() {
  if (typeof window === "undefined") return null;
  if (_instance) return _instance;
  const canvas = document.createElement("canvas");
  canvas.setAttribute("aria-hidden", "true");
  canvas.style.position = "fixed";
  canvas.style.top = "0";
  canvas.style.left = "0";
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  canvas.style.pointerEvents = "none";
  canvas.style.zIndex = "60";
  document.body.appendChild(canvas);
  _instance = confetti.create(canvas, { resize: true, useWorker: true });
  return _instance;
}

// Karine-luxe palette — restrained gold, champagne, warm ivory. Skipped
// the brand-jarring blues/reds you'd see in the default confetti mix.
const GOLD_PALETTE = ["#F4D577", "#C7982E", "#B8862B", "#EEDEC0", "#DFC06E"];

/**
 * Fire a two-burst gold confetti spray from the pill's on-screen
 * position (or top-right fallback when we can't measure it).
 */
export function burstFromEl(el) {
  const fire = getInstance();
  if (!fire) return;

  // Compute the origin as normalized viewport coords (0..1).
  let x = 0.85, y = 0.08;
  try {
    if (el && typeof el.getBoundingClientRect === "function") {
      const r = el.getBoundingClientRect();
      x = Math.min(0.98, Math.max(0.02, (r.left + r.width / 2) / window.innerWidth));
      y = Math.min(0.98, Math.max(0.02, (r.top + r.height / 2) / window.innerHeight));
    }
  } catch { /* silent */ }

  const base = {
    origin: { x, y },
    colors: GOLD_PALETTE,
    disableForReducedMotion: true, // respects OS-level a11y preference
    scalar: 0.85, // slightly smaller confetti = luxe not chaotic
    ticks: 160,
  };

  // Two staggered bursts so it reads as a bloom, not a bang.
  try {
    fire({ ...base, particleCount: 55, spread: 60, startVelocity: 32 });
    setTimeout(() => {
      try { fire({ ...base, particleCount: 35, spread: 90, startVelocity: 22, decay: 0.94 }); }
      catch { /* silent */ }
    }, 220);
  } catch { /* silent */ }
}

/**
 * Show the celebratory toast. `tier` is the API's { points, value, label }
 * object for the milestone the client just crossed.
 */
export function showMilestoneToast(tier, { language = "en" } = {}) {
  if (!tier) return;
  const value = Number(tier.value || 0);
  const label = tier.label || (value ? `$${value}` : "");
  const isFr = language === "fr";

  const title = isFr
    ? (value > 0 ? `Vous avez débloqué ${label} !` : "Nouveau palier débloqué !")
    : (value > 0 ? `You just unlocked ${label}!` : "New tier unlocked!");
  const body = isFr
    ? "Utilisez-le à votre prochain rendez-vous — sans expiration."
    : "Use it on your next appointment — never expires.";

  // sonner's `custom` renderer lets us match the Crown Reserve palette
  // instead of the default green success chip.
  toast.custom(
    (id) => (
      <div
        onClick={() => toast.dismiss(id)}
        role="button"
        tabIndex={0}
        data-testid="milestone-toast"
        style={{
          background: "linear-gradient(135deg, #1F1611 0%, #3D2E20 100%)",
          border: "1px solid rgba(212,165,116,0.55)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
          borderRadius: "14px",
          padding: "12px 14px",
          minWidth: "280px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          cursor: "pointer",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 30, height: 30, borderRadius: "999px",
            background: "linear-gradient(135deg,#F4D577,#C7982E)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 15, lineHeight: 1, color: "#1F1611", fontWeight: 700,
            flexShrink: 0,
          }}
        >♛</span>
        <div style={{ minWidth: 0 }}>
          <div style={{
            color: "#F4D577",
            fontFamily: "'Playfair Display', serif",
            fontSize: 15, fontWeight: 700, lineHeight: 1.2,
          }}>{title}</div>
          <div style={{
            color: "rgba(255,255,255,0.75)",
            fontSize: 11, marginTop: 2,
          }}>{body}</div>
        </div>
      </div>
    ),
    { duration: 5000, position: "top-center" }
  );
}
